import type { DesignOperationSubmission, DesignSystemCatalogEntry } from "@afar-research/contracts";
import { DesignOperationSubmission as DesignOperationSubmissionSchema } from "@afar-research/contracts";
import * as Schema from "effect/Schema";

import type { DesignActionId } from "./designActions.ts";
import { designSystemSlugFromMentionPath } from "./designSystemMentions.ts";

const isDesignOperationSubmission = Schema.is(DesignOperationSubmissionSchema);

export const DESIGN_WORKSPACE_RELATIVE_PATH = ".afar-research/design";

export interface DesignGitPathFingerprint {
  readonly path: string;
  readonly digest: string;
  readonly kind?: "file" | "directory" | "symlink" | "other" | "missing";
  readonly content?: string;
}

export interface DesignGitSnapshot {
  readonly filesystemMode?: "git" | "full";
  readonly headOid: string | null;
  readonly upstreamOid: string | null;
  readonly stagedFingerprint: string;
  readonly sourceControlRefsFingerprint: string;
  readonly stagedPaths: ReadonlyArray<string>;
  readonly changedPaths: ReadonlyArray<string>;
  readonly designFingerprints?: ReadonlyArray<DesignGitPathFingerprint>;
  readonly outsideDesignFingerprints: ReadonlyArray<DesignGitPathFingerprint>;
}

export type DesignGitVerificationFailure =
  | "commit-created"
  | "source-control-ref-changed"
  | "staged-changes"
  | "unexpected-worktree-change";

export interface DesignGitVerificationResult {
  readonly verified: boolean;
  readonly failures: ReadonlyArray<DesignGitVerificationFailure>;
}

export type DesignArtifactVerificationFailureCode =
  | "missing-design-md"
  | "invalid-design-md"
  | "missing-slug-identity"
  | "slug-changed"
  | "revision-conflict"
  | "missing-template"
  | "missing-example"
  | "missing-provenance"
  | "unintended-artifact"
  | "coequal-authority"
  | "legacy-systems-path";

export interface DesignArtifactVerificationFailure {
  readonly code: DesignArtifactVerificationFailureCode;
  readonly path: string | null;
  readonly detail: string;
}

export interface DesignArtifactVerificationResult {
  readonly verified: boolean;
  readonly failures: ReadonlyArray<DesignArtifactVerificationFailure>;
  readonly changedPaths: ReadonlyArray<string>;
  readonly designSystemSlug: string | null;
  readonly slug: string | null;
}

export type DesignOperationVerificationClassification =
  | "valid-create"
  | "valid-update"
  | "valid-add-template"
  | "valid-add-example"
  | "valid-create-sample"
  | "known-safe-failure"
  | "unresolved-partial-mutation";

export function classifyDesignOperationOutcome(input: {
  readonly action?: DesignActionId;
  readonly providerState: "completed" | "failed" | "interrupted" | "cancelled" | "aborted";
  readonly artifactsValid: boolean;
  readonly gitVerified: boolean;
  readonly designChanged: boolean;
}): {
  readonly status: "completed" | "failed" | "cancelled" | "unresolved";
  readonly classification: DesignOperationVerificationClassification;
} {
  // Git is the evidence, not the artifact check: when git verification held and
  // nothing under the Design Workspace changed, nothing was written, so there is
  // nothing half-applied whatever the artifact check wanted to find.
  const nothingWritten = input.gitVerified && !input.designChanged;
  const safe = input.artifactsValid && input.gitVerified;
  if (input.providerState === "completed" && safe) {
    const classification =
      input.action === "update-design"
        ? "valid-update"
        : input.action === "add-design-template"
          ? "valid-add-template"
          : input.action === "add-design-example"
            ? "valid-add-example"
            : input.action === "create-design-sample"
              ? "valid-create-sample"
              : "valid-create";
    return { status: "completed", classification };
  }
  // A turn that ended having written nothing, including one that ran to
  // completion. The Skill Bundles grill the user, so an operation's first turn
  // routinely ends with a question and an untouched Design Workspace: calling
  // that an unresolved partial mutation retains the checkout lease and reports
  // a failure for the most ordinary thing an operation does.
  if (nothingWritten) {
    return {
      status: input.providerState === "failed" ? "failed" : "cancelled",
      classification: "known-safe-failure",
    };
  }
  return { status: "unresolved", classification: "unresolved-partial-mutation" };
}

const isDesignPath = (path: string): boolean =>
  path === DESIGN_WORKSPACE_RELATIVE_PATH || path.startsWith(`${DESIGN_WORKSPACE_RELATIVE_PATH}/`);

const fingerprintKey = (entries: ReadonlyArray<DesignGitPathFingerprint>) =>
  JSON.stringify([...entries].toSorted((left, right) => left.path.localeCompare(right.path)));

export function designChangedSinceSnapshot(input: {
  readonly before: DesignGitSnapshot;
  readonly after: DesignGitSnapshot;
}): boolean {
  if (input.before.designFingerprints && input.after.designFingerprints) {
    return (
      fingerprintKey(input.before.designFingerprints) !==
      fingerprintKey(input.after.designFingerprints)
    );
  }
  const designPaths = (snapshot: DesignGitSnapshot) =>
    snapshot.changedPaths.filter(isDesignPath).toSorted();
  return JSON.stringify(designPaths(input.before)) !== JSON.stringify(designPaths(input.after));
}

export function verifyDesignGitOutcome(input: {
  readonly before: DesignGitSnapshot;
  readonly after: DesignGitSnapshot;
}): DesignGitVerificationResult {
  const failures: DesignGitVerificationFailure[] = [];
  if (
    input.before.filesystemMode !== undefined &&
    input.after.filesystemMode !== input.before.filesystemMode
  ) {
    failures.push("unexpected-worktree-change");
  }
  if (input.after.headOid !== input.before.headOid) failures.push("commit-created");
  if (
    input.after.upstreamOid !== input.before.upstreamOid ||
    input.after.sourceControlRefsFingerprint !== input.before.sourceControlRefsFingerprint
  ) {
    failures.push("source-control-ref-changed");
  }
  const stagedBaseline = new Set(input.before.stagedPaths);
  if (
    input.after.stagedFingerprint !== input.before.stagedFingerprint ||
    input.after.stagedPaths.some((path) => !stagedBaseline.has(path))
  ) {
    failures.push("staged-changes");
  }
  const baseline = new Set(input.before.changedPaths);
  if (input.after.changedPaths.some((path) => !baseline.has(path) && !isDesignPath(path))) {
    failures.push("unexpected-worktree-change");
  }
  if (
    fingerprintKey(input.before.outsideDesignFingerprints) !==
      fingerprintKey(input.after.outsideDesignFingerprints) &&
    !failures.includes("unexpected-worktree-change")
  ) {
    failures.push("unexpected-worktree-change");
  }
  return { verified: failures.length === 0, failures };
}

const OPTIONAL_ORIENTATION = new Set(["README.md"]);
const OPTIONAL_DERIVED = new Set(["design_tokens.json", "tailwind.config.js"]);

export interface ParsedDesignSystemDocument {
  readonly name: string;
  readonly slug: string;
  readonly body: string;
  readonly frontmatter: Readonly<Record<string, string>>;
}

function parseDesignSystemFrontmatterValue(value: string): string | null {
  if (value.startsWith('"')) {
    try {
      const decoded = JSON.parse(value) as unknown;
      return typeof decoded === "string" ? decoded : null;
    } catch {
      return null;
    }
  }
  return value.replace(/^'|'$/g, "");
}

export function parseDesignSystemDocument(content: string): ParsedDesignSystemDocument | null {
  const trimmed = content.replace(/^\uFEFF/, "");
  if (!trimmed.startsWith("---\n") && !trimmed.startsWith("---\r\n")) return null;
  const end = trimmed.indexOf("\n---", 4);
  if (end < 0) return null;
  const frontmatterBlock = trimmed.slice(4, end).replace(/\r/g, "");
  const body = trimmed.slice(end + 4).replace(/^\r?\n/, "");
  const frontmatter: Record<string, string> = {};
  for (const line of frontmatterBlock.split("\n")) {
    const match = /^([A-Za-z][A-Za-z0-9_-]*):\s*(.*)$/.exec(line.trim());
    if (!match) continue;
    const value = parseDesignSystemFrontmatterValue(match[2]!.trim());
    if (value === null) return null;
    frontmatter[match[1]!] = value;
  }
  const name = frontmatter.name ?? "";
  const slug = frontmatter.slug ?? "";
  if (name.length === 0 || slug.length === 0) return null;
  return { name, slug, body, frontmatter };
}

/** Stable content revision for expectedRevision checks (not the Git path fingerprint). */
export function designSystemContentRevision(content: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5 ^ content.length;
  for (let i = 0; i < content.length; i++) {
    const c = content.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x01000193) ^ (h1 >>> 16);
  }
  return `${(h1 >>> 0).toString(16).padStart(8, "0")}${(h2 >>> 0).toString(16).padStart(8, "0")}:${content.length}`;
}

function designSystemRootPath(slug: string): string {
  return `${DESIGN_WORKSPACE_RELATIVE_PATH}/design-system/${slug}`;
}

function designMdPath(slug: string): string {
  return `${designSystemRootPath(slug)}/DESIGN.md`;
}

export type DesignSystemCatalogObservation =
  | { readonly slug: string; readonly kind: "invalid-root" }
  | { readonly slug: string; readonly kind: "missing-design-md" }
  | { readonly slug: string; readonly kind: "nonregular-design-md" }
  | { readonly slug: string; readonly kind: "content"; readonly contents: string };

export function classifyDesignSystemCatalogObservation(
  observation: DesignSystemCatalogObservation,
): DesignSystemCatalogEntry {
  const path = designMdPath(observation.slug);
  const invalid = (
    finding: string,
    identity: { readonly displayName?: string } = {},
    state: "invalid" | "upgrade-required" | "unsupported" = "invalid",
  ): DesignSystemCatalogEntry => ({
    slug: observation.slug,
    displayName: identity.displayName ?? observation.slug,
    expectedRevision: null,
    designMdPath: path,
    state,
    selection: { eligible: false, reason: finding },
    findings: [finding],
  });
  switch (observation.kind) {
    case "invalid-root":
      return invalid("Design System root must be a regular non-symlink directory.");
    case "missing-design-md":
      return invalid("DESIGN.md is missing.");
    case "nonregular-design-md":
      return invalid("DESIGN.md must be a regular non-symlink file.");
    case "content": {
      const parsed = parseDesignSystemDocument(observation.contents);
      if (parsed === null) {
        return invalid("DESIGN.md is missing valid name or slug frontmatter.");
      }
      if (parsed.slug !== observation.slug) {
        return invalid("DESIGN.md slug does not match its canonical directory.", {
          displayName: parsed.name,
        });
      }
      const declaredState = parsed.frontmatter.state;
      if (declaredState === "upgrade-required" || declaredState === "unsupported") {
        return invalid(
          `Design System declares state '${declaredState}' and is not selectable by this release.`,
          { displayName: parsed.name },
          declaredState,
        );
      }
      if (declaredState !== undefined && declaredState !== "draft" && declaredState !== "ready") {
        return invalid(
          `Design System declares unsupported state '${declaredState}'.`,
          { displayName: parsed.name },
          "unsupported",
        );
      }
      return {
        slug: parsed.slug,
        displayName: parsed.name,
        expectedRevision: designSystemContentRevision(observation.contents),
        designMdPath: path,
        state: declaredState === "draft" ? "draft" : "ready",
        selection: { eligible: true },
        findings: [],
      };
    }
  }
}

function isIntendedDesignArtifact(path: string, action: DesignActionId): boolean {
  const prefix = `${DESIGN_WORKSPACE_RELATIVE_PATH}/`;
  if (!path.startsWith(prefix)) return false;
  const relative = path.slice(prefix.length);
  if (relative === "design-system" || relative === "design-system/") return true;
  if (relative.startsWith("systems/")) return false;
  const match = /^design-system\/([^/]+)(?:\/(.+))?$/.exec(relative);
  if (!match) return false;
  const rest = match[2];
  if (rest === undefined) return true;
  if (rest === "DESIGN.md") return true;
  if (OPTIONAL_ORIENTATION.has(rest) || OPTIONAL_DERIVED.has(rest)) return true;
  if (action === "create-design" || action === "update-design") {
    return false;
  }
  if (rest === "templates") return action === "add-design-template";
  if (rest === "examples") return action === "add-design-example";
  if (rest === "outputs") return action === "create-design-sample";
  const templateMatch = /^templates\/([^/]+)(?:\/(.+))?$/.exec(rest);
  if (templateMatch) {
    if (action !== "add-design-template") return false;
    const file = templateMatch[2];
    return file === undefined || file === "TEMPLATE.md" || OPTIONAL_ORIENTATION.has(file);
  }
  const exampleMatch = /^examples\/([^/]+)(?:\/(.+))?$/.exec(rest);
  if (exampleMatch) {
    if (action !== "add-design-example") return false;
    const file = exampleMatch[2];
    if (file === undefined || file === "EXAMPLE.md" || OPTIONAL_ORIENTATION.has(file)) return true;
    return (
      file.startsWith("evidence/") &&
      !file.includes("..") &&
      file.split("/").every((segment) => segment.length > 0)
    );
  }
  const sampleMatch = /^outputs\/([^/]+)(?:\/(.+))?$/.exec(rest);
  if (sampleMatch) {
    if (action !== "create-design-sample") return false;
    const file = sampleMatch[2];
    return (
      file === undefined ||
      (!file.includes("..") && file.split("/").every((segment) => segment.length > 0))
    );
  }
  return false;
}

function changedPathsBetween(
  before: ReadonlyArray<DesignGitPathFingerprint>,
  after: ReadonlyArray<DesignGitPathFingerprint>,
): string[] {
  const beforeByPath = new Map(before.map((entry) => [entry.path, entry]));
  const afterByPath = new Map(after.map((entry) => [entry.path, entry]));
  return [
    ...new Set([
      ...after
        .filter((entry) => {
          const prior = beforeByPath.get(entry.path);
          return prior === undefined || prior.digest !== entry.digest;
        })
        .map((entry) => entry.path),
      ...before
        .filter((entry) => {
          const current = afterByPath.get(entry.path);
          return current === undefined || current.digest !== entry.digest;
        })
        .map((entry) => entry.path),
    ]),
  ].toSorted();
}

/** Product guidance when Design state is mismatched, corrupt, or conflicting. */
export const DESIGN_DELETE_AND_RECREATE_GUIDANCE =
  "Retry the Design action. AFAR brings the Design layout back to the expected shape automatically before it runs; only content it cannot regenerate needs to be rewritten by you.";

function findDesignMd(
  fingerprints: ReadonlyArray<DesignGitPathFingerprint>,
  slug: string,
): DesignGitPathFingerprint | undefined {
  return fingerprints.find((entry) => entry.path === designMdPath(slug));
}

function verifyCreateDesignArtifactOutcome(input: {
  readonly before: ReadonlyArray<DesignGitPathFingerprint>;
  readonly after: ReadonlyArray<DesignGitPathFingerprint>;
  readonly operation: Extract<DesignOperationSubmission, { action: "create-design" }>;
  readonly changedPaths: ReadonlyArray<string>;
  readonly changed: ReadonlyArray<DesignGitPathFingerprint>;
}): DesignArtifactVerificationResult {
  const failures: DesignArtifactVerificationFailure[] = [];

  for (const path of input.changedPaths) {
    if (path.includes("/systems/") || path.endsWith("/systems")) {
      failures.push({
        code: "legacy-systems-path",
        path,
        detail: "Design Systems must live under design-system/<slug>/, not systems/.",
      });
      continue;
    }
    if (!isIntendedDesignArtifact(path, "create-design")) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: "Create Design System may only write intended Design Workspace artifacts.",
      });
    }
  }

  const designMdEntries = input.changed.filter((entry) =>
    /^\.afar-research\/design\/design-system\/[^/]+\/DESIGN\.md$/.test(entry.path),
  );
  if (designMdEntries.length === 0) {
    failures.push({
      code: "missing-design-md",
      path: null,
      detail: "Create Design System must write design-system/<slug>/DESIGN.md.",
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  if (designMdEntries.length > 1) {
    failures.push({
      code: "unintended-artifact",
      path: designMdEntries[1]!.path,
      detail: "Create Design System must create exactly one DESIGN.md authority.",
    });
  }

  const designMd = designMdEntries[0]!;
  const parsed =
    designMd.content !== undefined ? parseDesignSystemDocument(designMd.content) : null;
  if (parsed === null) {
    failures.push({
      code: "invalid-design-md",
      path: designMd.path,
      detail: "DESIGN.md must include YAML frontmatter with display name and storage slug.",
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  if (parsed.name !== input.operation.displayName) {
    failures.push({
      code: "invalid-design-md",
      path: designMd.path,
      detail: `DESIGN.md name must match the requested display name (${input.operation.displayName}).`,
    });
  }
  const expectedPath = designMdPath(parsed.slug);
  if (designMd.path !== expectedPath) {
    failures.push({
      code: "invalid-design-md",
      path: designMd.path,
      detail: `DESIGN.md storage path must match frontmatter slug (${parsed.slug}).`,
    });
  }

  const targetDirectory = designSystemRootPath(parsed.slug);
  if (
    input.before.some(
      (entry) => entry.path === targetDirectory || entry.path.startsWith(`${targetDirectory}/`),
    )
  ) {
    failures.push({
      code: "unintended-artifact",
      path: designMd.path,
      detail: "Create Design System requires a previously absent storage slug.",
    });
  }
  const designSystemRoot = `${DESIGN_WORKSPACE_RELATIVE_PATH}/design-system`;
  for (const path of input.changedPaths) {
    if (
      path !== designSystemRoot &&
      path !== targetDirectory &&
      !path.startsWith(`${targetDirectory}/`)
    ) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: "Create Design System may not modify or delete artifacts outside its new slug.",
      });
    }
  }

  for (const entry of input.changed) {
    const base = entry.path.split("/").pop() ?? "";
    if (
      (base === "design_tokens.json" || base === "tailwind.config.js") &&
      !input.changedPaths.includes(designMdPath(parsed.slug))
    ) {
      failures.push({
        code: "coequal-authority",
        path: entry.path,
        detail: "Derived projections are not coequal authorities and require DESIGN.md.",
      });
    }
  }

  return {
    verified: failures.length === 0,
    failures,
    changedPaths: input.changedPaths,
    designSystemSlug: parsed.slug,
    slug: parsed.slug,
  };
}

/**
 * The storage slug an agent-backed Design Operation targets, read off the
 * Design System Mention it carries. A mention that is not a canonical authority
 * path cannot name a Design System, so verification fails rather than guessing.
 */
function operationTargetSlug(operation: { readonly target: { readonly path: string } }) {
  return designSystemSlugFromMentionPath(operation.target.path);
}

/**
 * The Design Artifact Group the turn actually wrote, discovered from the paths
 * it changed rather than dictated by the submission. The artifact's name is
 * established by the Skill Bundle during the turn, so verification learns it
 * here — and a turn that scattered writes across more than one group is exactly
 * what this catches.
 */
function discoverArtifactGroup(input: {
  readonly collectionDirectory: string;
  readonly changedPaths: ReadonlyArray<string>;
}):
  | { readonly ok: true; readonly directory: string }
  | { readonly ok: false; readonly groups: ReadonlyArray<string> } {
  const prefix = `${input.collectionDirectory}/`;
  const groups = new Set<string>();
  for (const path of input.changedPaths) {
    if (!path.startsWith(prefix)) continue;
    const segment = path.slice(prefix.length).split("/")[0];
    if (segment !== undefined && segment.length > 0) groups.add(segment);
  }
  const found = [...groups];
  return found.length === 1
    ? { ok: true, directory: `${input.collectionDirectory}/${found[0]}` }
    : { ok: false, groups: found };
}

function verifyUpdateDesignArtifactOutcome(input: {
  readonly before: ReadonlyArray<DesignGitPathFingerprint>;
  readonly after: ReadonlyArray<DesignGitPathFingerprint>;
  readonly operation: Extract<DesignOperationSubmission, { action: "update-design" }>;
  readonly changedPaths: ReadonlyArray<string>;
  readonly changed: ReadonlyArray<DesignGitPathFingerprint>;
}): DesignArtifactVerificationResult {
  const failures: DesignArtifactVerificationFailure[] = [];
  const slug = operationTargetSlug(input.operation);
  if (slug === null) {
    return {
      verified: false,
      failures: [
        {
          code: "missing-slug-identity",
          path: input.operation.target.path,
          detail: "The Design System Mention does not address a canonical DESIGN.md authority.",
        },
      ],
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  const targetDirectory = designSystemRootPath(slug);
  const beforeDesignMd = findDesignMd(input.before, slug);
  if (beforeDesignMd?.content === undefined) {
    failures.push({
      code: "missing-design-md",
      path: designMdPath(slug),
      detail: "Update Design System requires an existing DESIGN.md authority.",
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  const beforeParsed = parseDesignSystemDocument(beforeDesignMd.content);
  if (beforeParsed === null || beforeParsed.slug !== slug) {
    failures.push({
      code: "missing-slug-identity",
      path: beforeDesignMd.path,
      detail: "Update Design System requires the expected Design System storage slug.",
    });
  }
  // No revision check. Update is the operation that actually loses something:
  // the guard used to refuse a turn whose DESIGN.md had moved underneath it,
  // and nothing detects that now. What is left in its place is narrower and
  // still worth having — the slug must survive and DESIGN.md must genuinely
  // change — so a concurrent edit is overwritten rather than caught. That is
  // the same trade Rename made when it became agent-driven, taken knowingly.

  for (const path of input.changedPaths) {
    if (path.includes("/systems/") || path.endsWith("/systems")) {
      failures.push({
        code: "legacy-systems-path",
        path,
        detail: "Design Systems must live under design-system/<slug>/, not systems/.",
      });
      continue;
    }
    if (!isIntendedDesignArtifact(path, "update-design")) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: "Update Design System may only change intended Design System artifacts.",
      });
      continue;
    }
    if (path !== targetDirectory && !path.startsWith(`${targetDirectory}/`)) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: "Update Design System may not modify artifacts outside the selected system.",
      });
    }
  }

  const afterDesignMd = findDesignMd(input.after, slug);
  if (afterDesignMd?.content === undefined) {
    failures.push({
      code: "missing-design-md",
      path: designMdPath(slug),
      detail: "Update Design System must retain design-system/<slug>/DESIGN.md.",
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: slug,
      slug,
    };
  }
  const afterParsed = parseDesignSystemDocument(afterDesignMd.content);
  if (afterParsed === null) {
    failures.push({
      code: "invalid-design-md",
      path: afterDesignMd.path,
      detail: "DESIGN.md must include YAML frontmatter with display name and storage slug.",
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: slug,
      slug,
    };
  }
  if (afterParsed.slug !== slug) {
    failures.push({
      code: "slug-changed",
      path: afterDesignMd.path,
      detail:
        "Update Design System must preserve the storage slug identity (rename is a separate action).",
    });
  }
  if (!input.changedPaths.includes(afterDesignMd.path)) {
    failures.push({
      code: "invalid-design-md",
      path: afterDesignMd.path,
      detail: "Update Design System must change canonical DESIGN.md content.",
    });
  }

  return {
    verified: failures.length === 0,
    failures,
    changedPaths: input.changedPaths,
    designSystemSlug: afterParsed.slug,
    slug: afterParsed.slug,
  };
}

function verifyAddLibraryArtifactOutcome(input: {
  readonly before: ReadonlyArray<DesignGitPathFingerprint>;
  readonly after: ReadonlyArray<DesignGitPathFingerprint>;
  readonly operation: Extract<
    DesignOperationSubmission,
    { action: "add-design-template" | "add-design-example" | "create-design-sample" }
  >;
  readonly changedPaths: ReadonlyArray<string>;
}): DesignArtifactVerificationResult {
  const failures: DesignArtifactVerificationFailure[] = [];
  const slug = operationTargetSlug(input.operation);
  if (slug === null) {
    return {
      verified: false,
      failures: [
        {
          code: "missing-slug-identity",
          path: input.operation.target.path,
          detail: "The Design System Mention does not address a canonical DESIGN.md authority.",
        },
      ],
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  const targetDirectory = designSystemRootPath(slug);
  const beforeDesignMd = findDesignMd(input.before, slug);
  if (beforeDesignMd?.content === undefined) {
    failures.push({
      code: "missing-design-md",
      path: designMdPath(slug),
      detail: `${input.operation.action} requires an existing DESIGN.md authority.`,
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: null,
      slug: null,
    };
  }
  const beforeParsed = parseDesignSystemDocument(beforeDesignMd.content);
  if (beforeParsed === null || beforeParsed.slug !== slug) {
    failures.push({
      code: "missing-slug-identity",
      path: beforeDesignMd.path,
      detail: "The selected Design System slug must match DESIGN.md.",
    });
  }

  const collection =
    input.operation.action === "add-design-template"
      ? "templates"
      : input.operation.action === "add-design-example"
        ? "examples"
        : "outputs";
  const collectionDirectory = `${targetDirectory}/${collection}`;
  // The artifact's name is not in the submission — the Skill Bundle establishes
  // it during the turn — so verification discovers the group the turn wrote
  // instead of predicting it, and treats a scattered write as the failure.
  // This is why the three Adds lost nothing with the revision check: they must
  // not rewrite DESIGN.md at all, which is checked below and is strictly
  // stronger than refusing the turn when DESIGN.md had moved. Sample gains the
  // most — its helper renames a colliding slug to a timestamped sibling, so a
  // predicted directory was wrong exactly when the collision mattered.
  const discovered = discoverArtifactGroup({
    collectionDirectory,
    changedPaths: input.changedPaths,
  });
  if (!discovered.ok) {
    failures.push({
      code: "unintended-artifact",
      path: collectionDirectory,
      detail:
        discovered.groups.length === 0
          ? `${input.operation.action} must write one artifact under ${collectionDirectory}/.`
          : `${input.operation.action} must write exactly one artifact, not ${discovered.groups.length}.`,
    });
    return {
      verified: false,
      failures,
      changedPaths: input.changedPaths,
      designSystemSlug: slug,
      slug,
    };
  }
  const artifactDirectory = discovered.directory;
  const artifactSlug = artifactDirectory.slice(collectionDirectory.length + 1);

  for (const path of input.changedPaths) {
    if (path.includes("/systems/") || path.endsWith("/systems")) {
      failures.push({
        code: "legacy-systems-path",
        path,
        detail: "Design Systems must live under design-system/<slug>/, not systems/.",
      });
      continue;
    }
    if (!isIntendedDesignArtifact(path, input.operation.action)) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: `${input.operation.action} may only write intended library artifacts under the selected Design System.`,
      });
      continue;
    }
    if (
      path !== collectionDirectory &&
      path !== artifactDirectory &&
      !path.startsWith(`${artifactDirectory}/`)
    ) {
      failures.push({
        code: "unintended-artifact",
        path,
        detail: "Library artifacts must stay inside the selected artifact directory.",
      });
    }
  }

  const afterDesignMd = findDesignMd(input.after, slug);
  if (afterDesignMd?.content === undefined) {
    failures.push({
      code: "missing-design-md",
      path: designMdPath(slug),
      detail: "Adding library artifacts must not remove DESIGN.md.",
    });
  } else {
    const afterParsed = parseDesignSystemDocument(afterDesignMd.content);
    if (afterParsed === null || afterParsed.slug !== slug) {
      failures.push({
        code: "slug-changed",
        path: afterDesignMd.path,
        detail: "Adding library artifacts must preserve the Design System storage slug.",
      });
    }
    if (input.changedPaths.includes(afterDesignMd.path)) {
      failures.push({
        code: "unintended-artifact",
        path: afterDesignMd.path,
        detail:
          input.operation.action === "create-design-sample"
            ? "Creating a Design Sample must not rewrite DESIGN.md (use update-design)."
            : "Adding a template or example must not rewrite DESIGN.md (use update-design).",
      });
    }
  }

  if (input.operation.action === "add-design-template") {
    const templateSlug = artifactSlug;
    const templatePath = `${artifactDirectory}/TEMPLATE.md`;
    const created = input.after.find((entry) => entry.path === templatePath);
    if (created === undefined || !input.changedPaths.includes(templatePath)) {
      failures.push({
        code: "missing-template",
        path: templatePath,
        detail: `Add Design Template must write ${templatePath}.`,
      });
    } else if (
      input.before.some(
        (entry) =>
          entry.path === templatePath ||
          entry.path.startsWith(`${targetDirectory}/templates/${templateSlug}/`),
      )
    ) {
      failures.push({
        code: "unintended-artifact",
        path: templatePath,
        detail: "Add Design Template requires a previously absent template slug.",
      });
    } else if (
      created.content === undefined ||
      !hasLibraryArtifactProvenance({
        content: created.content,
        slug: templateSlug,
        designSystemSlug: slug,
      })
    ) {
      failures.push({
        code: "missing-provenance",
        path: templatePath,
        detail: "TEMPLATE.md must retain typed Design System identity provenance.",
      });
    }
  } else if (input.operation.action === "add-design-example") {
    const exampleSlug = artifactSlug;
    const examplePath = `${artifactDirectory}/EXAMPLE.md`;
    const created = input.after.find((entry) => entry.path === examplePath);
    if (created === undefined || !input.changedPaths.includes(examplePath)) {
      failures.push({
        code: "missing-example",
        path: examplePath,
        detail: `Add Design Example must write ${examplePath}.`,
      });
    } else if (
      input.before.some(
        (entry) =>
          entry.path === examplePath ||
          entry.path.startsWith(`${targetDirectory}/examples/${exampleSlug}/`),
      )
    ) {
      failures.push({
        code: "unintended-artifact",
        path: examplePath,
        detail: "Add Design Example requires a previously absent example slug.",
      });
    } else if (
      created.content === undefined ||
      !hasLibraryArtifactProvenance({
        content: created.content,
        slug: exampleSlug,
        designSystemSlug: slug,
      })
    ) {
      failures.push({
        code: "missing-provenance",
        path: examplePath,
        detail: "EXAMPLE.md must retain typed Design System identity provenance.",
      });
    }
  } else {
    const sampleSlug = artifactSlug;
    const sampleReadmePath = `${artifactDirectory}/README.md`;
    const created = input.after.find((entry) => entry.path === sampleReadmePath);
    if (created === undefined || !input.changedPaths.includes(sampleReadmePath)) {
      failures.push({
        code: "missing-example",
        path: sampleReadmePath,
        detail: `Create Design Sample must write ${sampleReadmePath}.`,
      });
    } else if (
      input.before.some(
        (entry) =>
          entry.path === sampleReadmePath ||
          entry.path.startsWith(`${targetDirectory}/outputs/${sampleSlug}/`),
      )
    ) {
      failures.push({
        code: "unintended-artifact",
        path: sampleReadmePath,
        detail: "Create Design Sample requires a previously absent sample slug.",
      });
    }
  }

  return {
    verified: failures.length === 0,
    failures,
    changedPaths: input.changedPaths,
    designSystemSlug: beforeParsed?.slug ?? null,
    slug: beforeParsed?.slug ?? slug,
  };
}

/**
 * Whether an artifact's frontmatter carries the identity provenance the Design
 * skills write. The name is checked for presence rather than for a value: the
 * Skill Bundle establishes it with the user during the turn, so the submission
 * never knew it. `slug` and `designSystemSlug` are still exact — they are the
 * storage identity, and a rename now follows them through every artifact.
 */
function hasLibraryArtifactProvenance(input: {
  readonly content: string;
  readonly slug: string;
  readonly designSystemSlug: string;
}): boolean {
  const normalized = input.content.replace(/\r/g, "");
  if (!normalized.startsWith("---\n")) return false;
  const end = normalized.indexOf("\n---", 4);
  if (end < 0) return false;
  const fields = new Map<string, string>();
  for (const line of normalized.slice(4, end).split("\n")) {
    const match = /^([A-Za-z][A-Za-z0-9_-]*):\s*(.*)$/.exec(line.trim());
    if (match) fields.set(match[1]!, match[2]!.trim().replace(/^["']|["']$/g, ""));
  }
  return (
    (fields.get("name")?.length ?? 0) > 0 &&
    fields.get("slug") === input.slug &&
    fields.get("designSystemSlug") === input.designSystemSlug
  );
}

export function verifyDesignArtifactOutcome(input: {
  readonly before: ReadonlyArray<DesignGitPathFingerprint>;
  readonly after: ReadonlyArray<DesignGitPathFingerprint>;
  readonly operation: DesignOperationSubmission;
}): DesignArtifactVerificationResult {
  const beforeByPath = new Map(input.before.map((entry) => [entry.path, entry]));
  const changed = input.after.filter((entry) => {
    const prior = beforeByPath.get(entry.path);
    return prior === undefined || prior.digest !== entry.digest;
  });
  const changedPaths = changedPathsBetween(input.before, input.after);

  switch (input.operation.action) {
    case "create-design":
      return verifyCreateDesignArtifactOutcome({
        before: input.before,
        after: input.after,
        operation: input.operation,
        changedPaths,
        changed,
      });
    case "update-design":
      return verifyUpdateDesignArtifactOutcome({
        before: input.before,
        after: input.after,
        operation: input.operation,
        changedPaths,
        changed,
      });
    case "add-design-template":
    case "add-design-example":
    case "create-design-sample":
      return verifyAddLibraryArtifactOutcome({
        before: input.before,
        after: input.after,
        operation: input.operation,
        changedPaths,
      });
    case "rename-design-system":
    case "delete-design-system":
      return {
        verified: false,
        failures: [
          {
            code: "unintended-artifact",
            path: null,
            detail:
              "Host Design rename/delete mutations are verified by DesignSystems, not Artifact Outcome.",
          },
        ],
        changedPaths,
        designSystemSlug: null,
        slug: null,
      };
  }
}

export interface DesignOperationActivity {
  readonly id: string;
  readonly kind: string;
  readonly payload: unknown;
  readonly sequence?: number | undefined;
  readonly turnId?: string | null | undefined;
  readonly createdAt: string;
}

export interface DesignOperationQueuedPayload {
  readonly operationId: string;
  readonly action: DesignActionId;
  readonly detail: string;
  readonly baseline: DesignGitSnapshot | null;
  readonly input?: DesignOperationSubmission;
}

export interface DesignOperationStartedPayload extends Omit<
  DesignOperationQueuedPayload,
  "baseline"
> {
  readonly baseline: DesignGitSnapshot;
  readonly turnId: string;
}

export interface DesignOperationOutcomePayload {
  readonly operationId: string;
  readonly outcome: string;
  readonly classification?: DesignOperationVerificationClassification;
  readonly designSystemSlug?: string | null;
  readonly slug?: string | null;
  readonly verification?: DesignOperationVerificationEvidence;
}

export interface DesignOperationVerificationEvidence {
  readonly artifactsVerified: boolean;
  readonly gitVerified: boolean;
  readonly designChanged: boolean;
  readonly changedPaths: ReadonlyArray<string>;
  readonly workspaceFailures: ReadonlyArray<string>;
  readonly artifactFailures: ReadonlyArray<DesignArtifactVerificationFailure>;
  readonly gitFailures: ReadonlyArray<DesignGitVerificationFailure>;
}

export type DesignOperationEvent =
  | { readonly kind: "design.operation.queued"; readonly payload: DesignOperationQueuedPayload }
  | {
      readonly kind: "design.operation.baselined";
      readonly payload: DesignOperationQueuedPayload;
    }
  | { readonly kind: "design.operation.started"; readonly payload: DesignOperationStartedPayload }
  | {
      readonly kind:
        | "design.operation.completed"
        | "design.operation.failed"
        | "design.operation.cancelled"
        | "design.operation.unresolved";
      readonly payload: DesignOperationOutcomePayload;
    };

/**
 * The last Design Operation to settle in a thread, as a value that changes when
 * one does — or `null` when none has.
 *
 * Every settled operation has written, moved, or removed something under the
 * Design Workspace, so anything the App read from it beforehand is describing
 * the past: a Design System listing taken before a Rename still names the old
 * one at the old path. Callers hold this and re-read when it changes.
 */
export function latestSettledDesignOperationId(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): string | null {
  const settledKinds = new Set([
    "design.operation.completed",
    "design.operation.failed",
    "design.operation.cancelled",
    "design.operation.unresolved",
  ]);
  let latest: string | null = null;
  for (const activity of activities) {
    if (!settledKinds.has(activity.kind)) continue;
    const payload = activity.payload;
    if (typeof payload !== "object" || payload === null || !("operationId" in payload)) continue;
    const operationId = (payload as { readonly operationId: unknown }).operationId;
    if (typeof operationId !== "string") continue;
    latest = `${activity.kind}:${operationId}`;
  }
  return latest;
}

/**
 * The Design Operations a thread left unresolved, newest state wins.
 *
 * An operation dispatched onto an idle thread has no Thread Queue Entry to read
 * this off, and unresolved authority has to survive a service restart: the
 * checkout it may have half-written stays claimed until a human accepts or
 * repairs it. Reading the operation's own activities is the only account that
 * holds for a queued operation and a direct one alike.
 */
export function selectUnresolvedDesignOperationIds(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): ReadonlyArray<string> {
  const terminalKinds = new Set([
    "design.operation.completed",
    "design.operation.failed",
    "design.operation.cancelled",
    "design.operation.unresolved",
  ]);
  const stateByOperationId = new Map<string, string>();
  for (const activity of activities) {
    if (!activity.kind.startsWith("design.operation.")) continue;
    const payload = activity.payload;
    if (typeof payload !== "object" || payload === null || !("operationId" in payload)) continue;
    const operationId = (payload as { readonly operationId: unknown }).operationId;
    if (typeof operationId !== "string") continue;
    if (!terminalKinds.has(activity.kind)) {
      stateByOperationId.set(operationId, "open");
      continue;
    }
    stateByOperationId.set(operationId, activity.kind);
  }
  return [...stateByOperationId.entries()]
    .filter(([, state]) => state === "design.operation.unresolved")
    .map(([operationId]) => operationId);
}

export function queueDesignOperation(input: {
  readonly operationId: string;
  readonly action: DesignActionId;
  readonly detail: string;
  readonly input?: DesignOperationSubmission;
}): Extract<DesignOperationEvent, { kind: "design.operation.queued" }> {
  return {
    kind: "design.operation.queued",
    payload: {
      operationId: input.operationId,
      action: input.action,
      detail: input.detail,
      baseline: null,
      ...(input.input !== undefined ? { input: input.input } : {}),
    },
  };
}

export function startDesignOperation(
  queued: DesignOperationQueuedPayload,
  turnId: string,
  baseline: DesignGitSnapshot,
): Extract<DesignOperationEvent, { kind: "design.operation.started" }> {
  return { kind: "design.operation.started", payload: { ...queued, baseline, turnId } };
}

export function recordDesignOperationBaseline(
  queued: DesignOperationQueuedPayload,
  baseline: DesignGitSnapshot,
): Extract<DesignOperationEvent, { kind: "design.operation.baselined" }> {
  return { kind: "design.operation.baselined", payload: { ...queued, baseline } };
}

export function finishDesignOperation(input: {
  readonly operationId: string;
  readonly outcome: string;
  readonly status: "completed" | "failed" | "cancelled" | "unresolved";
  readonly classification?: DesignOperationVerificationClassification;
  readonly designSystemSlug?: string | null;
  readonly slug?: string | null;
  readonly verification?: DesignOperationVerificationEvidence;
}): DesignOperationEvent {
  return {
    kind: `design.operation.${input.status}`,
    payload: {
      operationId: input.operationId,
      outcome: input.outcome,
      ...(input.classification !== undefined ? { classification: input.classification } : {}),
      ...(input.designSystemSlug !== undefined ? { designSystemSlug: input.designSystemSlug } : {}),
      ...(input.slug !== undefined ? { slug: input.slug } : {}),
      ...(input.verification !== undefined ? { verification: input.verification } : {}),
    },
  };
}

export function findPendingDesignOperation(
  activities: ReadonlyArray<DesignOperationActivity>,
  turnId: string,
): {
  readonly operationId: string;
  readonly action: DesignActionId;
  readonly baseline: DesignGitSnapshot | null;
  readonly input: DesignOperationSubmission | null;
} | null {
  const operations = new Map<
    string,
    {
      operationId: string;
      action: DesignActionId;
      baseline: DesignGitSnapshot | null;
      input: DesignOperationSubmission | null;
      turnId: string | null;
      finished: boolean;
    }
  >();
  for (const activity of activities) {
    if (!activity.kind.startsWith("design.operation.")) continue;
    const payload = activity.payload as Record<string, unknown>;
    if (activity.kind === "design.operation.queued") {
      const operationId = String(payload.operationId ?? "");
      operations.set(operationId, {
        operationId,
        action: (payload.action as DesignActionId) ?? "create-design",
        baseline: null,
        input: isDesignOperationSubmission(payload.input) ? payload.input : null,
        turnId: null,
        finished: false,
      });
      continue;
    }
    if (activity.kind === "design.operation.baselined") {
      const operationId = String(payload.operationId ?? "");
      const current = operations.get(operationId);
      if (current) current.baseline = (payload.baseline as DesignGitSnapshot | null) ?? null;
      continue;
    }
    if (activity.kind === "design.operation.started") {
      const operationId = String(payload.operationId ?? "");
      const current = operations.get(operationId) ?? {
        operationId,
        action: (payload.action as DesignActionId) ?? "create-design",
        baseline: null,
        input: isDesignOperationSubmission(payload.input) ? payload.input : null,
        turnId: null,
        finished: false,
      };
      current.baseline = payload.baseline as DesignGitSnapshot;
      current.turnId = String(payload.turnId ?? activity.turnId ?? "");
      current.input = isDesignOperationSubmission(payload.input) ? payload.input : current.input;
      operations.set(operationId, current);
      continue;
    }
    if (
      activity.kind === "design.operation.completed" ||
      activity.kind === "design.operation.failed" ||
      activity.kind === "design.operation.cancelled" ||
      activity.kind === "design.operation.unresolved"
    ) {
      const operationId = String(payload.operationId ?? "");
      const current = operations.get(operationId);
      if (current) current.finished = true;
    }
  }
  for (const operation of operations.values()) {
    if (!operation.finished && operation.turnId === turnId) {
      return {
        operationId: operation.operationId,
        action: operation.action,
        baseline: operation.baseline,
        input: operation.input,
      };
    }
  }
  return null;
}
